import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

type Props = {
  show: boolean;
  onCancel?: () => void;
  tips?: string[];
};

const defaultTips = [
  "This can take a few minutes…",
  "Feel free to grab a coffee ☕",
  "Don’t close or refresh this tab",
  "We’re checking every user’s last activity",
];

export default function LoaderOverlay({ show, onCancel, tips = defaultTips }: Props) {
  const [idx, setIdx] = useState(0);

  // Rotate status lines
  useEffect(() => {
    if (!show) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % tips.length), 3500);
    return () => clearInterval(t);
  }, [show, tips.length]);

  // Optional: keep screen awake on supported browsers
  useEffect(() => {
    let lock: any;
    (async () => {
      try {
        if (show && "wakeLock" in navigator) {
          // @ts-ignore
          lock = await (navigator as any).wakeLock.request("screen");
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      try { lock?.release?.(); } catch {}
    };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[1000] bg-white/80 backdrop-blur-sm flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-full max-w-md bg-white border rounded-2xl shadow-xl p-6 text-center"
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
          >
            <div className="mx-auto mb-4 h-10 w-10 rounded-full border-4 border-black/15 border-t-black animate-spin" />
            <h3 className="text-lg font-semibold mb-2">Crunching activity data…</h3>

            <div className="h-6 overflow-hidden mb-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -12, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="text-sm text-gray-700"
                >
                  {tips[idx]}
                </motion.div>
              </AnimatePresence>
            </div>

            {onCancel && (
              <button
                onClick={onCancel}
                className="mt-2 text-sm px-4 py-2 rounded-md border hover:bg-black hover:text-white transition"
              >
                Cancel
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
